import type { MapImage } from "./mapDocument";
import { mapImageScale } from "./mapDocument";
import type { WorldPoint } from "./mapGeometry";
import {
  LANDMARK_HANDLES,
  clampResizeFactors,
  type LandmarkHandle,
  rotatePoint,
} from "./mapLandmarkTransform";
import { just, nothing, type Maybe } from "./maybe";

export function mapImageDrawSize(
  image: MapImage,
  zoom: number,
): { width: number; height: number } {
  const scale = mapImageScale(image) * zoom;
  return {
    width: image.width * scale * (image.scaleX ?? 1),
    height: image.height * scale * (image.scaleY ?? 1),
  };
}

export function mapImageLocalPoint(
  image: MapImage,
  point: WorldPoint,
): WorldPoint {
  return rotatePoint(
    { x: point.x - image.x, y: point.y - image.y },
    -(image.rotation ?? 0),
  );
}

export function hitTestMapImage(
  image: MapImage,
  world: WorldPoint,
  zoom: number,
  extraTolerancePx = 4,
): boolean {
  const size = mapImageDrawSize(image, 1);
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const tolerance = extraTolerancePx / z;
  const local = mapImageLocalPoint(image, world);
  return (
    Math.abs(local.x) <= size.width / 2 + tolerance &&
    Math.abs(local.y) <= size.height / 2 + tolerance
  );
}

export function mapImageWorldPoint(
  image: MapImage,
  point: WorldPoint,
): WorldPoint {
  const rotated = rotatePoint(point, image.rotation ?? 0);
  return { x: image.x + rotated.x, y: image.y + rotated.y };
}

export function mapImageCorners(image: MapImage): WorldPoint[] {
  const size = mapImageDrawSize(image, 1);
  return LANDMARK_HANDLES.filter(
    (handle) => handle.x !== 0 && handle.y !== 0,
  ).map((handle) =>
    mapImageWorldPoint(image, {
      x: (handle.x * size.width) / 2,
      y: (handle.y * size.height) / 2,
    }),
  );
}

export function mapImageHandles(
  image: MapImage,
  zoom: number,
): { id: LandmarkHandle; point: WorldPoint }[] {
  const size = mapImageDrawSize(image, 1);
  return [
    ...LANDMARK_HANDLES.map((handle) => ({
      id: handle.id,
      point: mapImageWorldPoint(image, {
        x: (handle.x * size.width) / 2,
        y: (handle.y * size.height) / 2,
      }),
    })),
    {
      id: "rotate",
      point: mapImageWorldPoint(image, {
        x: 0,
        y: -size.height / 2 - 26 / zoom,
      }),
    },
  ];
}

export function hitMapImageHandle(
  image: MapImage,
  point: WorldPoint,
  zoom: number,
): Maybe<LandmarkHandle> {
  const hit = mapImageHandles(image, zoom).find(
    (handle) =>
      Math.hypot(point.x - handle.point.x, point.y - handle.point.y) <=
      8 / zoom,
  );
  return typeof hit === "undefined" ? nothing : just(hit.id);
}

/** Transform from the pointer-down snapshot so repeated pointer events never accumulate error. */
export function transformMapImage(
  start: MapImage,
  handle: LandmarkHandle,
  pointer: WorldPoint,
  pointerStart: WorldPoint,
  shift: boolean,
): MapImage {
  if (handle === "rotate") {
    const angle =
      ((Math.atan2(pointer.y - start.y, pointer.x - start.x) -
        Math.atan2(pointerStart.y - start.y, pointerStart.x - start.x)) *
        180) /
        Math.PI +
      (start.rotation ?? 0);
    const rotation = shift ? Math.round(angle / 15) * 15 : angle;
    return { ...start, rotation: ((rotation % 360) + 360) % 360 };
  }
  const direction = LANDMARK_HANDLES.find((item) => item.id === handle)!;
  const size = mapImageDrawSize(start, 1);
  const anchor = {
    x: (-direction.x * size.width) / 2,
    y: (-direction.y * size.height) / 2,
  };
  const pointerLocal = mapImageLocalPoint(start, pointer);
  const startLocal = mapImageLocalPoint(start, pointerStart);
  const local = {
    x: (direction.x * size.width) / 2 + pointerLocal.x - startLocal.x,
    y: (direction.y * size.height) / 2 + pointerLocal.y - startLocal.y,
  };
  let rx =
    direction.x === 0 ? 1 : (local.x - anchor.x) / (direction.x * size.width);
  let ry =
    direction.y === 0 ? 1 : (local.y - anchor.y) / (direction.y * size.height);
  const sx = (start.size ?? 1) * (start.scaleX ?? 1);
  const sy = (start.size ?? 1) * (start.scaleY ?? 1);
  const clamped = clampResizeFactors(
    sx,
    sy,
    start.width,
    start.height,
    rx,
    ry,
    direction.x !== 0 && direction.y !== 0 && shift === false,
  );
  rx = clamped.rx;
  ry = clamped.ry;
  const center = mapImageWorldPoint(start, {
    x: anchor.x + (direction.x * size.width * rx) / 2,
    y: anchor.y + (direction.y * size.height * ry) / 2,
  });
  return {
    ...start,
    ...center,
    scaleX: (start.scaleX ?? 1) * Math.abs(rx),
    scaleY: (start.scaleY ?? 1) * Math.abs(ry),
    flipX: (start.flipX === true) !== rx < 0,
    flipY: (start.flipY === true) !== ry < 0,
  };
}
